import dynamic from 'next/dynamic';

import { ModalProvider, type ModalType } from './ModalProvider';

const AccountMenu = dynamic(async () => (await import('./AccountMenu')).AccountMenu);

interface Props {
  hasThemeSupport?: boolean;
  /**
   * This function returns a boolean to allow pages to block the thread if there are unsaved changes
   * By returning true, we'll switch the user's team and send them to the homepage.
   *
   * This is needed because the `beforeunload` event doesnt fire until after we've made the request
   * to switch the user's teams reroute them to the homepage.
   *
   * @example
   *
   * ```tsx
   * <UserProfileMenu
   *  onConfirmTeamSwitch={() => {
   *    if (unsavedChanges) {
   *      // Ask the user if they want to navigate without saving their changes
   *      return confirm(...);
   *    }
   *    // Otherwise, we can switch the user's team and route them to the home page
   *    return true;
   *  }}
   * />
   * ```
   */
  onConfirmTeamSwitch?: () => boolean;
}

export const AccountMenuWithModals = ({ hasThemeSupport = true, onConfirmTeamSwitch }: Props) => {
  return (
    <ModalProvider>
      {({ setModal }) => (
        <AccountMenu
          setModal={(modal: ModalType) => setModal(modal)}
          hasThemeSupport={hasThemeSupport}
          onConfirmTeamSwitch={onConfirmTeamSwitch}
        />
      )}
    </ModalProvider>
  );
};
